import Fuse from 'fuse.js';
import { deburr } from 'lodash-es';

export type ResolveStatus = 'matched' | 'ambiguous' | 'unmatched';

export interface RankedCandidate<T> {
  record: T;
  /** Fuse score, 0 = perfect … 1 = worst. */
  score: number;
}

export interface ResolveResult<T> {
  status: ResolveStatus;
  top: T | null;
  candidates: RankedCandidate<T>[];
}

export interface ResolveOptions {
  /** Fuse cutoff; hits worse than this are discarded. */
  threshold?: number;
  /** A top hit at or under this score, clear of the runner-up, is a confident match. */
  strong?: number;
  /** Minimum score gap between the top hit and the runner-up to call it unambiguous. */
  margin?: number;
  /**
   * Cutoff for the nearest names offered when nothing clears {@link threshold}.
   * These are suggestions, never a match: the record stays unresolved until the
   * rep picks one. Recognition mis-hears a product or a surname often enough that
   * a mention with no options is a dead end for them.
   */
  suggest?: number;
  /**
   * Transform the spoken mention into the text actually searched — e.g. drop an
   * honorific the rep said but the record does not store. The displayed mention
   * stays the original; only the search needle changes. Defaults to identity.
   */
  prepareNeedle?: (mention: string) => string;
}

/**
 * Resolve a spoken text mention against a set of records the rep already owns.
 *
 * Pure and synchronous: callers pass the offline collection (contacts, accounts,
 * brands, key messages) and a projection that yields the searchable name variants
 * for each record. The rep confirms the outcome in the review screen — the LLM
 * never picks the record.
 */
const DEFAULTS: Required<ResolveOptions> = {
  threshold: 0.4, strong: 0.2, margin: 0.1, suggest: 0.75, prepareNeedle: (m) => m,
};

/** How many near names an unresolved mention offers; a longer list is not a choice. */
const MAX_SUGGESTIONS = 5;

/** Fold accents, dash variants, case, and whitespace so spoken text lines up with stored names. */
function normalize(text: string): string {
  return deburr(text)
    .replace(/[‐-―−]/g, '-')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function resolveMention<T>(
  mention: string,
  records: T[],
  project: (record: T) => string[],
  opts: ResolveOptions = {},
): ResolveResult<T> {
  const { threshold, strong, margin, suggest, prepareNeedle } = { ...DEFAULTS, ...opts };
  const empty: ResolveResult<T> = { status: 'unmatched', top: null, candidates: [] };

  const needle = normalize(prepareNeedle(mention));
  if (!needle || records.length === 0) return empty;

  const rows: { record: T; name: string }[] = [];
  for (const record of records) {
    for (const variant of project(record)) {
      const name = normalize(variant);
      if (name) rows.push({ record, name });
    }
  }
  if (rows.length === 0) return empty;

  const fuse = new Fuse(rows, {
    keys: ['name'],
    includeScore: true,
    shouldSort: true,
    threshold,
    distance: 100,
  });

  // Collapse the per-variant hits to the best score per record.
  const bestByRecord = new Map<T, number>();
  for (const hit of fuse.search(needle) as { item: { record: T }; score?: number }[]) {
    const { record } = hit.item;
    const score = hit.score ?? 1;
    const prior = bestByRecord.get(record);
    if (prior === undefined || score < prior) bestByRecord.set(record, score);
  }

  const candidates: RankedCandidate<T>[] = [...bestByRecord]
    .map(([record, score]) => ({ record, score }))
    .sort((a, b) => a.score - b.score);
  if (candidates.length === 0) {
    return { status: 'unmatched', top: null, candidates: nearest(rows, needle, suggest) };
  }

  const [top, second] = candidates;
  const confident = top.score <= strong && (!second || second.score - top.score >= margin);
  if (confident) {
    return { status: 'matched', top: top.record, candidates };
  }
  return { status: 'ambiguous', top: top.record, candidates: widen(candidates, nearest(rows, needle, suggest)) };
}

/**
 * The ranked candidates, extended with near names that did not clear the match
 * threshold.
 *
 * A mention the matcher could not settle has landed on the wrong record often
 * enough that its own shortlist is not a choice. The record it settled on stays
 * first; the rest are there so the rep can reach the one that was meant.
 */
function widen<T>(candidates: RankedCandidate<T>[], suggestions: RankedCandidate<T>[]): RankedCandidate<T>[] {
  const seen = new Set(candidates.map((c) => c.record));
  const extra = suggestions.filter((s) => !seen.has(s.record));
  return [...candidates, ...extra].slice(0, MAX_SUGGESTIONS);
}

/**
 * The nearest stored names to a mention nothing matched, ranked.
 *
 * Searched wider than a match allows, because the rep needs somewhere to go when
 * the recogniser mis-hears a name. Offered as choices only; nothing here is
 * treated as resolved.
 */
function nearest<T>(
  rows: { record: T; name: string }[],
  needle: string,
  cutoff: number,
): RankedCandidate<T>[] {
  const loose = new Fuse(rows, { keys: ['name'], includeScore: true, shouldSort: true, threshold: cutoff, distance: 100 });
  const bestByRecord = new Map<T, number>();
  for (const hit of loose.search(needle) as { item: { record: T }; score?: number }[]) {
    const { record } = hit.item;
    const score = hit.score ?? 1;
    const prior = bestByRecord.get(record);
    if (prior === undefined || score < prior) bestByRecord.set(record, score);
  }
  return [...bestByRecord]
    .map(([record, score]) => ({ record, score }))
    .sort((a, b) => a.score - b.score)
    .slice(0, MAX_SUGGESTIONS);
}

/** One detected name paired with how it resolved — the row shape the review screen renders. */
export interface ResolvedMention<T> {
  mention: string;
  status: ResolveStatus;
  top: T | null;
  candidates: RankedCandidate<T>[];
}

/**
 * Resolve a batch of detected names against one collection, keeping each mention
 * alongside its outcome. Blank and duplicate mentions (case-insensitive) are
 * dropped so the rep never confirms the same person twice.
 */
export function resolveMentions<T>(
  mentions: string[],
  records: T[],
  project: (record: T) => string[],
  opts: ResolveOptions = {},
): ResolvedMention<T>[] {
  const seen = new Set<string>();
  const resolved: ResolvedMention<T>[] = [];
  for (const raw of mentions ?? []) {
    const mention = (raw ?? '').trim();
    if (!mention) continue;
    const key = mention.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const { status, top, candidates } = resolveMention(mention, records, project, opts);
    resolved.push({ mention, status, top, candidates });
  }
  return resolved;
}
